import { useState } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQSectionProps {
  title: string;
  items: FAQItem[];
  image?: string;
  imageAlt?: string;
}

export default function FAQSection({ title, items, image, imageAlt }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-off-white py-28 lg:py-40">
      <div className="mx-auto px-6 lg:px-12 xl:px-20">
        <div
          ref={ref}
          className={`transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex flex-col lg:flex-row lg:items-start gap-16 lg:gap-24">
            {/* Left sticky column */}
            <div className="lg:w-5/12 flex-shrink-0 lg:sticky lg:top-28 flex flex-col gap-8">
              <div>
                <div className="flex items-center gap-4 mb-5">
                  <span className="block w-8 h-px bg-gold flex-shrink-0" />
                  <span className="text-gold text-[9px] font-semibold tracking-[0.38em] uppercase">
                    Common Questions
                  </span>
                </div>
                <h2
                  className="font-serif text-charcoal leading-tight"
                  style={{ fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', fontWeight: 300 }}
                >
                  {title}
                </h2>
                <div className="w-10 h-px bg-gold mt-6" />
              </div>

              {/* Image below heading */}
              {image && (
                <div className="relative overflow-hidden" style={{ height: '340px' }}>
                  <img
                    src={image}
                    alt={imageAlt ?? 'Southeast Lighting & Design — commercial project'}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/30 via-transparent to-transparent" />
                  <div className="absolute inset-0 border border-charcoal/8 pointer-events-none" />
                </div>
              )}
            </div>

            {/* Accordion */}
            <div className="lg:w-7/12 space-y-0">
              {items.map((item, index) => (
                <FAQAccordion
                  key={index}
                  item={item}
                  isOpen={openIndex === index}
                  onToggle={() => setOpenIndex(openIndex === index ? null : index)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQAccordion({
  item,
  isOpen,
  onToggle,
}: {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={`border-b border-charcoal/8 transition-colors duration-300 ${
        isOpen ? 'bg-white' : 'bg-transparent hover:bg-white/60'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-start justify-between px-6 py-6 text-left cursor-pointer"
      >
        <div className="flex items-start gap-4 pr-4">
          <span
            className={`flex-shrink-0 w-0.5 mt-1 transition-all duration-300 ${
              isOpen ? 'bg-gold h-full' : 'bg-charcoal/15 h-4'
            }`}
            style={{ minHeight: '16px' }}
          />
          <span
            className={`font-medium text-sm leading-snug transition-colors duration-200 ${
              isOpen ? 'text-charcoal' : 'text-charcoal/70'
            }`}
          >
            {item.question}
          </span>
        </div>

        <div
          className={`w-7 h-7 flex-shrink-0 flex items-center justify-center border transition-all duration-300 mt-0.5 ${
            isOpen
              ? 'border-gold bg-gold text-charcoal rotate-45'
              : 'border-charcoal/15 text-charcoal/40 hover:border-gold hover:text-gold'
          }`}
        >
          <i className="ri-add-line text-xs"></i>
        </div>
      </button>

      {isOpen && (
        <div className="px-6 pb-7 pl-[2.75rem]">
          <p className="text-stone text-sm leading-[1.95]">
            {item.answer}
          </p>
        </div>
      )}
    </div>
  );
}
